import React, {useState, useRef} from "react";
import {useForm} from "react-hook-form";
import {Bars} from "react-loader-spinner";

import Backdrop from "@material-ui/core/Backdrop";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card/Card";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";

import {ApolloAxios, ApiResponse, getResultType, isSuccessApi} from "../../atom/Apollo_ApiRequest3";
import {
  apollo_ad_client_icon_approval_path,
} from "../../atom/Apollo_links";
import {ApolloMessage, CustomizedSnackbars} from "../../atom/Apollo_Mesasge";
import {
  apollo_useStyles,
  options_reviewreason_ad_client_icon,
} from "../../atom/Apollo_styles";
import {ApolloBasicSelect, ApolloBasicTextField} from "../../molecule/Apollo_BaseItem";
import {AdClientIconApprovalInfo} from "./AccountModel"

type AdClientIconApprovalProp = {
  adClientIconId: number,
  submitAction: any,
  goBackAction: any,
}

/** 
 * 画面名：広告主アカウント作成/編集（共通部品）
 */
const AdClientIconApprovalEdit = ({
  adClientIconId,
  submitAction,
  goBackAction,
}: AdClientIconApprovalProp) => {

  const initAdClientIconInfo: AdClientIconApprovalInfo = {
    adClientIconId: 0,
    accountId: 0,
    accountName: "",
    advertiserName: "",
    industoryName: "",
    productName: "",
    adClientIconName: "",
    imageUrl: "",
    approvedAt: "",
    approval: 0,
    denialReason: 0,
    denialReasonDetail: "",
  }

  const classes = apollo_useStyles();
  const {data, setmsg} = React.useContext(ApolloMessage);

  const [adClientIconApproval, setAdClientIconApproval] = useState<AdClientIconApprovalInfo>(initAdClientIconInfo);
  const [isApproved, setIsApproved] = useState(false);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [open, setOpen] = React.useState(false);
  const [progress, setProgress] = useState(false);
  const processing = useRef(false);

  React.useEffect(() => {
    if (adClientIconId === 0) return;

    const init = async() => {
      setProgress(true);
      await getAdClientIconApproval();
      setProgress(false);
    }
    init();
  }, [adClientIconId]);

  const getAdClientIconApproval = async() => {
    const url = `${apollo_ad_client_icon_approval_path}/${adClientIconId}`;
    await ApolloAxios.get(
      url
    ).then(response => {
      const res: ApiResponse = response.data;
      if (!isSuccessApi(res.resultCode)) {
        return Promise.reject({
          open: true,
          type: getResultType(res.resultCode),
          errmsg: res?.message ? res.message : "広告主アイコン審査情報の取得に失敗しました",
        });
      }
      setAdClientIconApproval(res.result);
      setIsApproved(res.result.approval === 1 ? false : true);

      const image = new Image();
      image.src = res.result.imageUrl
      setImageWidth(image.width)
      setImageHeight(image.height)
    }).catch(err => {
      setmsg({
        type: "setMessage",
        payload: {
          ...data,
          open: true,
          type: err.type,
          message: err.errmsg,
        }
      });
    });
  }

  const handleSetApproval = (approval: number) => {
    setAdClientIconApproval({
      ...adClientIconApproval,
      approval: approval,
      denialReason: approval === 2 ? 0 : adClientIconApproval.denialReason,
      denialReasonDetail: approval === 2 ? "" : adClientIconApproval.denialReasonDetail,
    })
  }

  const handleSave = async() => {
    if (processing.current) return;

    processing.current = true;
    await submitAction(adClientIconApproval);
    processing.current = false;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const {
    register,
    handleSubmit,
    formState,
    control,
    errors,
  } = useForm({reValidateMode: 'onBlur'});
  const onSubmit = handleSubmit(async (formData: any) => {
    if (formState.isSubmitting) return;

    if (adClientIconApproval.approval !== 2 && adClientIconApproval.approval !== 3) {
      setmsg({
        type: "setMessage",
        payload: {
          ...data,
          open: true,
          type: "error",
          message: "否認結果を選択してください",
        }
      });
      return;
    }

    setOpen(true);
  })

  return (
    <React.Fragment>
      <CustomizedSnackbars/>
      <Backdrop className={classes.backdrop2} open={progress}>
        <Bars color="#00BFFF" height={80} width={80}/>
      </Backdrop>
      <form noValidate onSubmit={onSubmit}>
        <Card className={classes.root} square variant="outlined">
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="accountName"
                  label="広告主アカウント名"
                  value={adClientIconApproval.accountName}
                  inputProps={{readOnly: true,}}
                />
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="advertiserName"
                  label="広告主名"
                  value={adClientIconApproval.advertiserName}
                  inputProps={{readOnly: true,}}
                />
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="industoryName"
                  label="業種"
                  value={adClientIconApproval.industoryName}
                  inputProps={{readOnly: true,}}
                />
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="productName"
                  label="商品名称"
                  value={adClientIconApproval.productName}
                  inputProps={{readOnly: true,}}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid container xs={12}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle1" gutterBottom component="div" color="inherit">
                      アップロードファイル
                    </Typography>
                    <aside style={{marginTop: '1em'}}>
                      <img src={adClientIconApproval.imageUrl} style={{width: '40%'}}/>
                    </aside>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle1" gutterBottom component="div" color="inherit">
                      アイコンイメージ
                    </Typography>
                    <aside style={{marginTop: '1em'}}>
                      <img src={adClientIconApproval.imageUrl} style={{width: '40%', borderRadius: '50%', borderStyle: 'dotted'}}/>
                    </aside>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="width"
                  label="幅（画像選択後自動入力）"
                  variant="filled"
                  value={imageWidth}
                  inputProps={{type: 'number', readOnly: true,}}
                  inputRef={register}
                />
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="height"
                  label="高さ（画像選択後自動入力）"
                  variant="filled"
                  value={imageHeight}
                  inputProps={{type: 'number', readOnly: true,}}
                  inputRef={register}
                />
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="iconFileName"
                  label="ファイル名（画像選択後自動入力）"
                  variant="filled"
                  value={adClientIconApproval.adClientIconName}
                  inputProps={{readOnly: true,}}
                  inputRef={register}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom component="div" color="inherit">
                  否認結果
                </Typography>
                <Button
                  type="button"
                  className={classes.button}
                  style={adClientIconApproval.approval === 3
                    ? {color: "#000000", backgroundColor: "#e0e0e0"}
                    : {color: "#ffffff", backgroundColor: "Green"}}
                  disabled={isApproved}
                  onClick={e => handleSetApproval(2)}
                  variant="contained"
                >
                  承認
                </Button>
                <Button
                  type="button"
                  className={classes.button}
                  style={adClientIconApproval.approval === 2
                    ? {color: "#000000", backgroundColor: "#e0e0e0"}
                    : {color: "#ffffff", backgroundColor: "Red"}}
                  disabled={isApproved}
                  onClick={e => handleSetApproval(3)}
                  variant="contained"
                >
                  否認
                </Button>
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicSelect
                  id="denialReason"
                  label="否認理由"
                  options={options_reviewreason_ad_client_icon}
                  value={adClientIconApproval.denialReason}
                  disabled={isApproved || adClientIconApproval.approval !== 3}
                  readOnly={false}
                  control={control}
                  errors={errors}
                  setState={(e: number) => setAdClientIconApproval({
                    ...adClientIconApproval,
                    denialReason: e,
                  })}
                  rules={{
                    validate: (e: number) => {
                      if (adClientIconApproval.approval === 3 && adClientIconApproval.denialReason === 0) {
                        return "必須入力項目です。"
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  id="denialReasonDetail"
                  label="否認詳細"
                  value={adClientIconApproval.denialReasonDetail}
                  error={errors.denialReasonDetail}
                  disabled={isApproved || adClientIconApproval.approval !== 3}
                  inputRef={register({
                    maxLength: {
                      value: 255,
                      message: "255文字以内で入力して下さい。"
                    },
                  })}
                  setState={(e: string) => setAdClientIconApproval({
                    ...adClientIconApproval,
                    denialReasonDetail: e,
                  })}
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            {
              !isApproved
              ? <Button
                type="submit"
                color="primary"
                className={classes.button}
                onClick={onSubmit}
                variant="contained"
                disabled={processing.current || formState.isSubmitting}
              >
                登録
              </Button>
              :undefined
            }
            <Button
              variant="contained"
              color="inherit"
              onClick={goBackAction}
              className={classes.button}
              disabled={processing.current}
            >
              戻る
            </Button>
          </CardActions>
        </Card>

        <Dialog
          open={open}
          onClose={handleClose}
        >
          <DialogContent>
            <DialogContentText>
              審査結果を"{adClientIconApproval.approval === 2 ? "承認" : "否認"}"で登録します。
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              color="primary"
              className={classes.button}
              onClick={async() => {
                handleClose();
                await handleSave();
              }}
              variant="contained"
              disabled={processing.current || formState.isSubmitting}
            >
              登録
            </Button>
            <Button
              variant="contained"
              onClick={handleClose}
              className={classes.button}
              style={{color: "#ffffff", backgroundColor: "Red"}}
              disabled={processing.current}
            >
              戻る
            </Button>
          </DialogActions>
        </Dialog>

      </form>
    </React.Fragment>
  );
}
export default AdClientIconApprovalEdit;
